import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Title from "../../components/Title";
import Loader from "../../components/Loader";
import useScrollToTop from "../../hooks/useScrollToTop";
import { getRegistrationForm } from "../../api/voiceTalents/getRegistrationForm";
import { registerVoiceTalent } from "../../api/voiceTalents/registerVoiceTalent";

const CHOICE_SINGLE = ["select", "radio"];
const CHOICE_MULTI = ["multiselect", "checkbox"];
// Audio uploads are handled by the dedicated samples section below.
const SKIP_TYPES = ["file", "audio"];

const emptySample = () => ({ file: null, title: "", performanceType: "" });

const RegisterVoiceTalent = () => {
  const lng = useSelector((state) => state.lang.value);
  const { t } = useTranslation();
  useScrollToTop();

  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [performanceOptions, setPerformanceOptions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [samples, setSamples] = useState([emptySample()]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    getRegistrationForm()
      .then((data) => {
        setFields(data.fields || []);
        setPerformanceOptions(data.performance_type_options || []);
      })
      .catch((error) => console.error("Error fetching form", error))
      .finally(() => setLoading(false));
  }, []);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleMulti = (key, optionValue) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: current.includes(optionValue)
          ? current.filter((v) => v !== optionValue)
          : [...current, optionValue],
      };
    });
  };

  const updateSample = (index, patch) => {
    setSamples((prev) =>
      prev.map((sample, i) => (i === index ? { ...sample, ...patch } : sample))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess("");
    setSubmitting(true);

    try {
      const usableSamples = samples.filter((sample) => sample.file);
      const response = await registerVoiceTalent({
        answers,
        samples: usableSamples,
      });
      setSuccess(response.message || t("Your registration has been received."));
      setAnswers({});
      setSamples([emptySample()]);
      e.target.reset();
    } catch (error) {
      if (error.response && error.response.status === 422) {
        setErrors(error.response.data.errors || {});
      } else {
        setErrors({ general: [t("Something went wrong. Please try again.")] });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const errorFor = (key) => errors[`answers.${key}`];

  const renderField = (field) => {
    if (SKIP_TYPES.includes(field.type)) return null;

    const label = field.label[lng];
    const placeholder = field.placeholder ? field.placeholder[lng] : "";
    const value = answers[field.key] ?? (CHOICE_MULTI.includes(field.type) ? [] : "");

    return (
      <div key={field.key} className="flex flex-col gap-[6px]">
        <label className="text-purple font-medium">
          {label}
          {field.required && <span className="text-orange"> *</span>}
        </label>

        {field.type === "textarea" && (
          <textarea
            value={value}
            placeholder={placeholder}
            onChange={(e) => setAnswer(field.key, e.target.value)}
            className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none min-h-[100px]"
          />
        )}

        {["text", "email", "phone", "number"].includes(field.type) && (
          <input
            type={
              field.type === "email"
                ? "email"
                : field.type === "number"
                ? "number"
                : field.type === "phone"
                ? "tel"
                : "text"
            }
            value={value}
            placeholder={placeholder}
            onChange={(e) => setAnswer(field.key, e.target.value)}
            className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none"
          />
        )}

        {CHOICE_SINGLE.includes(field.type) && (
          <select
            value={value}
            onChange={(e) => setAnswer(field.key, e.target.value)}
            className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none"
          >
            <option value="">{placeholder || t("Select")}</option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label[lng]}
              </option>
            ))}
          </select>
        )}

        {CHOICE_MULTI.includes(field.type) && (
          <div className="flex flex-wrap gap-[12px]">
            {field.options.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-[6px] text-gray cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.value)}
                  onChange={() => toggleMulti(field.key, option.value)}
                />
                {option.label[lng]}
              </label>
            ))}
          </div>
        )}

        {errorFor(field.key) && (
          <span className="text-orange text-sm">{errorFor(field.key)[0]}</span>
        )}
      </div>
    );
  };

  if (loading) return <Loader />;

  return (
    <div dir={lng === "en" ? "ltr" : "rtl"} className="overflow-hidden">
      <div className="container mx-auto mt-[20px] mb-[140px] flex flex-col gap-[30px]">
        <Title title={t("Join as a Voice Talent")} />

        {success ? (
          <div className="bg-silver text-purple rounded-xl p-[30px] text-center text-lg">
            {success}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-[20px] max-w-[720px]"
          >
            {errors.general && (
              <div className="text-orange">{errors.general[0]}</div>
            )}

            {fields.map(renderField)}

            <div className="flex flex-col gap-[12px] border-t-[1px] border-purpleLight pt-[20px]">
              <h3 className="text-purple font-semibold text-lg">
                {t("Voice Samples")}
              </h3>

              {samples.map((sample, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-[8px] border-[1px] border-purpleLight rounded-lg p-[16px]"
                >
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={(e) =>
                      updateSample(index, { file: e.target.files[0] || null })
                    }
                  />
                  <input
                    type="text"
                    placeholder={t("Sample title")}
                    value={sample.title}
                    onChange={(e) =>
                      updateSample(index, { title: e.target.value })
                    }
                    className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none"
                  />
                  {performanceOptions.length > 0 && (
                    <select
                      value={sample.performanceType}
                      onChange={(e) =>
                        updateSample(index, { performanceType: e.target.value })
                      }
                      className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none"
                    >
                      <option value="">{t("Performance Type")}</option>
                      {performanceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label[lng]}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => setSamples((prev) => [...prev, emptySample()])}
                className="self-start text-purpleLight underline"
              >
                {t("Add another sample")}
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="self-start bg-purple text-white rounded-full px-[32px] py-[12px] hover:bg-purpleLight transition-all disabled:opacity-50"
            >
              {submitting ? t("Submitting...") : t("Submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegisterVoiceTalent;
